import ReactApexChart from 'react-apexcharts'

const COLORS = [
  '#6366f1',
  '#f59e0b',
  '#10b981',
  '#ef4444',
  '#3b82f6',
  '#ec4899',
  '#14b8a6',
  '#8b5cf6',
  '#f97316',
  '#06b6d4',
  '#84cc16',
  '#a855f7',
  '#eab308',
  '#0ea5e9',
  '#f43f5e',
  '#22c55e',
]

// ── Format helpers ──────────────────────────────────────────────────────────

function formatValue(val, fmt) {
  if (val === null || val === undefined) return '—'
  const n = Number(val)
  if (isNaN(n)) return String(val)
  if (fmt === 'currency')
    return `$${n.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`
  if (fmt === 'percent') return `${(n * 100).toFixed(1)}%`
  return n.toLocaleString('en-US')
}

// ── Data mapper ─────────────────────────────────────────────────────────────

function mapRowsToChart(rows, config) {
  if (!rows?.length || !config) return null
  const {type, x_column, y_columns = [], group_by} = config

  if (type === 'metric') {
    const col = x_column || y_columns[0]
    return {value: rows[0]?.[col], label: col}
  }

  if (type === 'table') {
    return {columns: Object.keys(rows[0]), rows}
  }

  if (type === 'pie') {
    return {
      labels: rows.map((r) => String(r[x_column] ?? '')),
      series: rows.map((r) => Number(r[y_columns[0]] ?? 0)),
    }
  }

  // group_by pivot — deduplicate x labels, one series per group
  if (group_by) {
    const valueCol = y_columns[0]

    // Preserve order of x-axis labels as they appear in data
    const labelsOrdered = []
    const labelsSeen = new Set()
    for (const r of rows) {
      const lbl = String(r[x_column] ?? '')
      if (!labelsSeen.has(lbl)) {
        labelsOrdered.push(lbl)
        labelsSeen.add(lbl)
      }
    }

    // Unique groups preserving order
    const groupsOrdered = []
    const groupsSeen = new Set()
    for (const r of rows) {
      const g = String(r[group_by] ?? '')
      if (!groupsSeen.has(g)) {
        groupsOrdered.push(g)
        groupsSeen.add(g)
      }
    }

    // Build a lookup map for fast access
    const lookup = {}
    for (const r of rows) {
      const key = `${String(r[x_column] ?? '')}__${String(r[group_by] ?? '')}`
      lookup[key] = Number(r[valueCol] ?? 0)
    }

    const series = groupsOrdered.map((group, i) => ({
      name: group,
      data: labelsOrdered.map((label) => lookup[`${label}__${group}`] ?? 0),
      color: COLORS[i % COLORS.length],
    }))

    return {labels: labelsOrdered, series}
  }

  // Flat — one bar/line per row
  const labels = rows.map((r) => String(r[x_column] ?? ''))
  const series = y_columns.map((col, i) => ({
    name: col,
    data: rows.map((r) => Number(r[col] ?? 0)),
    color: COLORS[i % COLORS.length],
  }))

  return {labels, series}
}

// ── Shared apex options ─────────────────────────────────────────────────────

function baseOptions(labels, title, colFormats = {}, yCol = null) {
  const fmt = yCol ? colFormats[yCol] || 'number' : 'number'

  return {
    chart: {
      toolbar: {show: true},
      background: 'transparent',
      animations: {enabled: false},
      zoom: {enabled: true},
    },
    title: {
      text: title,
      style: {fontSize: '12px', fontFamily: 'IBM Plex Mono', color: '#5a7a5a'},
    },
    colors: COLORS,
    xaxis: {
      categories: labels,
      labels: {
        style: {colors: '#8aaa8a', fontSize: '11px'},
        rotate: labels.length > 8 ? -45 : 0,
        hideOverlappingLabels: true,
        trim: true,
        maxHeight: 80,
      },
      tickAmount: Math.min(labels.length, 12),
    },
    yaxis: {
      labels: {
        style: {colors: '#8aaa8a', fontSize: '11px'},
        formatter: (v) => formatValue(v, fmt),
      },
    },
    tooltip: {
      theme: 'light',
      y: {formatter: (v) => formatValue(v, fmt)},
    },
    grid: {borderColor: '#d0dcd0'},
    legend: {
      labels: {colors: '#5a7a5a'},
      position: 'bottom',
      fontSize: '12px',
    },
    dataLabels: {enabled: false},
  }
}

// ── Chart components ────────────────────────────────────────────────────────

function LineChart({data, title, colFormats, yCol}) {
  const options = {
    ...baseOptions(data.labels, title, colFormats, yCol),
    stroke: {curve: 'smooth', width: 2},
    markers: {size: data.series.length <= 5 ? 4 : 2},
  }
  return <ReactApexChart options={options} series={data.series} type='line' height={320} />
}

function BarChart({data, title, horizontal, colFormats, yCol}) {
  const height = horizontal ? Math.max(280, data.labels.length * 32) : 320
  // Single series (no group_by, one y_column) → color each bar individually
  // instead of every bar sharing the first palette color.
  const distributed = data.series.length === 1

  const options = {
    ...baseOptions(data.labels, title, colFormats, yCol),
    plotOptions: {
      bar: {horizontal, borderRadius: 3, columnWidth: '60%', barHeight: '70%', distributed},
    },
    legend: {
      ...baseOptions(data.labels, title, colFormats, yCol).legend,
      show: !distributed,
    },
  }
  if (horizontal) {
    options.xaxis.labels.rotate = 0
    options.xaxis.labels.maxWidth = 200
  }
  return <ReactApexChart options={options} series={data.series} type='bar' height={height} />
}

function PieChart({data, title, colFormats, yCol}) {
  const fmt = yCol ? colFormats[yCol] || 'number' : 'number'
  const options = {
    chart: {toolbar: {show: false}, background: 'transparent'},
    title: {text: title, style: {fontSize: '12px', fontFamily: 'IBM Plex Mono', color: '#5a7a5a'}},
    labels: data.labels,
    colors: COLORS,
    legend: {labels: {colors: '#5a7a5a'}, position: 'bottom'},
    tooltip: {y: {formatter: (v) => formatValue(v, fmt)}},
    dataLabels: {style: {fontSize: '11px'}},
  }
  return <ReactApexChart options={options} series={data.series} type='pie' height={320} />
}

function MetricCard({data, title, colFormats}) {
  const fmt = colFormats?.[data.label] || 'number'
  return (
    <div className='metric-card'>
      <div className='metric-value'>{formatValue(data.value, fmt)}</div>
      <div className='metric-label'>{title || data.label}</div>
    </div>
  )
}

function DataTable({rows, columns, colFormats = {}}) {
  if (!rows?.length) return null
  return (
    <div className='chart-table-wrap'>
      <table className='chart-table'>
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {columns.map((c) => {
                const fmt = colFormats[c]
                const val = row[c]
                return <td key={c}>{fmt ? formatValue(val, fmt) : val ?? '—'}</td>
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ── Main export ─────────────────────────────────────────────────────────────

export default function DataView({chartConfig, rowsSample}) {
  if (!chartConfig || !rowsSample?.length) return null

  const {type, title, show_table, y_columns = [], column_formats = {}} = chartConfig
  const yCol = y_columns[0] || null
  const data = mapRowsToChart(rowsSample, chartConfig)

  if (!data) return null

  return (
    <div className='dataview-wrap'>
      {type === 'line' && (
        <LineChart data={data} title={title} colFormats={column_formats} yCol={yCol} />
      )}
      {type === 'bar' && (
        <BarChart
          data={data}
          title={title}
          horizontal={false}
          colFormats={column_formats}
          yCol={yCol}
        />
      )}
      {type === 'bar_horizontal' && (
        <BarChart
          data={data}
          title={title}
          horizontal={true}
          colFormats={column_formats}
          yCol={yCol}
        />
      )}
      {type === 'pie' && (
        <PieChart data={data} title={title} colFormats={column_formats} yCol={yCol} />
      )}
      {type === 'metric' && <MetricCard data={data} title={title} colFormats={column_formats} />}
      {type === 'table' && (
        <DataTable
          rows={rowsSample}
          columns={Object.keys(rowsSample[0])}
          colFormats={column_formats}
        />
      )}

      {show_table && type !== 'table' && (
        <DataTable
          rows={rowsSample}
          columns={Object.keys(rowsSample[0])}
          colFormats={column_formats}
        />
      )}
    </div>
  )
}
